import { apiClient } from '@/api/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Quarter } from '@/config/cbamReportingPeriods'

export type CbamSimulationGood = {
  id: string
  cnCode: string
  country: string
  productionMethodCode: string
  description?: string | null
  netMassInTonnes: number
  isUsingDefaultEmissions: boolean
  defaultSpecificDirectEmbeddedEmissions?: number | null
  defaultSpecificIndirectEmbeddedEmissions?: number | null
  importDate: string
  quarter?: Quarter | null
  createdAt: string
  updatedAt?: string | null
  deletedAt?: string | null
  supplierEmissionsReportId?: string | null
  cbamSimulationId: string
}

type CreateCbamSimulationGoodPayload = {
  cnCode: string
  country: string
  description?: string
  netMassInTonnes: number
  isUsingDefaultEmissions: boolean
  importDate: string
  quarter?: Quarter | null
  defaultSpecificDirectEmbeddedEmissions?: number | null
  defaultSpecificIndirectEmbeddedEmissions?: number | null
  supplierEmissionsReportId?: string
}

type UpdateCbamSimulationGoodPayload = CreateCbamSimulationGoodPayload

const CBAM_SIMULATION_GOODS_QUERY_KEY = 'cbamSimulationGoods'

export const useGetAllCbamSimulationGoods = (
  organizationId: string,
  queryParams?: {
    cbamSimulationId?: string
  },
  enabled?: boolean
) => {
  return useQuery({
    queryKey: [CBAM_SIMULATION_GOODS_QUERY_KEY],
    queryFn: async () => {
      const response = await apiClient.get<CbamSimulationGood[]>(
        `/v1/organizations/${organizationId}/cbam-simulation-goods`,
        {
          params: {
            cbamSimulationId: queryParams?.cbamSimulationId
          }
        }
      )
      return response.data
    },
    enabled: enabled ?? false
  })
}

export const useGetAllCbamSimulationGoodsForOrganization = (organizationId: string) => {
  return useQuery({
    queryKey: [CBAM_SIMULATION_GOODS_QUERY_KEY, organizationId],
    queryFn: async () => {
      const response = await apiClient.get<CbamSimulationGood[]>(
        `/v1/organizations/${organizationId}/cbam-simulation-goods`
      )
      return response.data
    }
  })
}

export const useCreateCbamSimulationGood = (organizationId: string, simulationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [CBAM_SIMULATION_GOODS_QUERY_KEY, organizationId, simulationId],
    mutationFn: async (payload: CreateCbamSimulationGoodPayload) => {
      const response = await apiClient.post<CbamSimulationGood>(
        `/v1/organizations/${organizationId}/cbam-simulations/${simulationId}/cbam-simulation-goods`,
        payload
      )

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CBAM_SIMULATION_GOODS_QUERY_KEY]
      })
    }
  })
}

export const useUpdateCbamSimulationGood = (
  organizationId: string,
  simulationId: string,
  goodId: string
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [CBAM_SIMULATION_GOODS_QUERY_KEY, organizationId, simulationId, goodId],
    mutationFn: async (payload: UpdateCbamSimulationGoodPayload) => {
      const response = await apiClient.put<CbamSimulationGood>(
        `/v1/organizations/${organizationId}/cbam-simulations/${simulationId}/cbam-simulation-goods/${goodId}`,
        payload
      )

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CBAM_SIMULATION_GOODS_QUERY_KEY]
      })
    }
  })
}

export const useDeleteCbamSimulationGood = (
  organizationId: string,
  simulationId: string,
  goodId: string
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [CBAM_SIMULATION_GOODS_QUERY_KEY, simulationId, goodId],
    mutationFn: async () => {
      await apiClient.delete(
        `/v1/organizations/${organizationId}/cbam-simulations/${simulationId}/cbam-simulation-goods/${goodId}`
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CBAM_SIMULATION_GOODS_QUERY_KEY]
      })
    }
  })
}
